import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { parse } from 'csv-parse/browser/esm'
import { type ChangeEventHandler, useEffect, useMemo, useRef, useState } from 'react'
import ArrowBack from '~icons/material-symbols/arrow-back.svg'
import RestartAlt from '~icons/material-symbols/restart-alt.svg'
import { css } from '../../../../../styled-system/css'
import { countEligibleStores } from './-count-stores'
import { Checkbox } from '../../../../components/inputs/checkbox'
import { LoadingOverlay } from '../../../../components/loading-overlay'
import { TablePaginationToolbar } from '../../../../components/table-pagination-toolbar'
import { useGetAllCities, useGetAllFundSources, useGetFundSourceFilters, useGetMyCompanies, useSetFundSourceFilters } from '../../../../libs/admin/hooks'
import { button } from '../../../../styles/buttonStyles'
import { contentWrapper } from '../../../../styles/contentWrapper'
import { description, input, inputLabel, requiredFieldLabel } from '../../../../styles/inputStyles'
import { pageTitle, sectionWrapper, titleWrapper } from '../../../../styles/sectionStyles'
import { downloadCsvFile, escapeCsvCell } from '../../../../libs/utils/csv'
import { trimPrefecture } from '../../../../libs/utils/trim-prefecture'
import { table, tableCell, tableHead, tableHeadCell, tableRow, tableWrapper } from '../../../../styles/tableStyles'

export const Route = createFileRoute('/admin/fundSources/$fundSourceId/edit')({
  component: RouteComponent,
})

const fundSourceTypeLabels: Record<string, string> = {
  Distributable: '配布ポイント',
  StandaloneDistributable: '配布ポイント（外部）',
  AdminGrant: '管理コンソール発行ポイント',
}

const getFundSourceTypeLabel = (type?: string) => {
  if (!type) {
    return '-'
  }
  return fundSourceTypeLabels[type] ?? 'ミニアプリ連携ポイント'
}

const storeSelectionCsvHeaders = ['store_id', 'store_name', 'company_name'] as const

type AvailableStore = {
  storeId: string
  storeName: string
  companyName: string
  address: string
  city: string
}

type CsvMessage = {
  type: 'error' | 'info'
  text: string
}

const formatCsvMessage = (fileName: string, message: string) => {
  return `[${fileName}]: ${message}`
}

const buildStoreSelectionTemplateCsv = (stores: AvailableStore[]) => {
  const lines = [storeSelectionCsvHeaders.join(',')]
  for (const store of stores) {
    lines.push([
      escapeCsvCell(store.storeId),
      escapeCsvCell(store.storeName),
      escapeCsvCell(store.companyName),
    ].join(','))
  }
  return lines.join('\r\n')
}

const parseCsvRecords = async (text: string): Promise<string[][]> => {
  return new Promise((resolve, reject) => {
    parse(
      text,
      {
        bom: true,
        columns: false,
        delimiter: ',',
        record_delimiter: ['\n', '\r\n', '\r'],
        relax_column_count: true,
        skip_empty_lines: true,
        trim: true,
      },
      (error, records: string[][]) => {
        if (error) {
          reject(error)
          return
        }
        resolve(records)
      },
    )
  })
}

const useFundSourceData = () => {
  const { fundSourceId } = Route.useParams()
  const { data: fundSources, isLoading: isFundSourcesLoading } = useGetAllFundSources()
  const { data: filters, isLoading: isFiltersLoading } = useGetFundSourceFilters({ fundSourceId })
  const { data: cities, isLoading: isCitiesLoading } = useGetAllCities()
  const { data: companyWithStores, isLoading: isCompaniesLoading } = useGetMyCompanies()
  const fundSource = fundSources?.find((item) => item.id === fundSourceId)

  return {
    fundSourceId,
    fundSource,
    filters: filters ?? [],
    cities: cities ?? [],
    companyWithStores: companyWithStores ?? [],
    isLoading: isFundSourcesLoading || isFiltersLoading || isCitiesLoading || isCompaniesLoading,
  }
}

type FilterSelection = {
  allowAll: boolean
  allowCities: boolean
  allowStores: boolean
}

const updateSelectionState = (
  current: FilterSelection,
  updates: Partial<FilterSelection>,
): FilterSelection => {
  const draft = { ...current, ...updates }

  // allowAll turned on
  if (!current.allowAll && draft.allowAll) {
    return { allowAll: true, allowCities: false, allowStores: false }
  }

  // allowCities or allowStores turned on
  if ((!current.allowCities && draft.allowCities) || (!current.allowStores && draft.allowStores)) {
    return { ...draft, allowAll: false }
  }

  // all turned off
  if (!draft.allowAll && !draft.allowCities && !draft.allowStores) {
    return { allowAll: true, allowCities: false, allowStores: false }
  }

  return draft
}

function RouteComponent() {
  const { fundSourceId, fundSource, filters, cities, companyWithStores, isLoading } = useFundSourceData()
  const setFundSourceFilters = useSetFundSourceFilters()
  const storeCsvInputRef = useRef<HTMLInputElement | null>(null)
  const [selection, setSelection] = useState<FilterSelection>({
    allowAll: true,
    allowCities: false,
    allowStores: false,
  })
  const [selectedCities, setSelectedCities] = useState<string[]>([])
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([])
  const [storePage, setStorePage] = useState(0)
  const [storeNameQuery, setStoreNameQuery] = useState('')
  const [companyNameQuery, setCompanyNameQuery] = useState('')
  const [csvMessage, setCsvMessage] = useState<CsvMessage | null>(null)
  const hasInitialized = useRef(false)
  const storesPerPage = 10
  const navigate = useNavigate()

  const availableCities = useMemo(() => {
    return Array.from(new Set(cities))
  }, [cities])

  const availableStores = useMemo<AvailableStore[]>(() => {
    const rows = companyWithStores.flatMap((companyWithStoresItem) => {
      const company = companyWithStoresItem.company
      if (!company) {
        return []
      }
      return companyWithStoresItem.stores.map((store) => ({
        storeId: store.id,
        storeName: store.name ?? '',
        companyName: company.name ?? '',
        address: store.fullAddress ?? store.city ?? '-',
        city: store.city ?? '',
      }))
    })
    return rows.sort((a, b) => {
      const companyOrder = a.companyName.localeCompare(b.companyName)
      if (companyOrder !== 0) {
        return companyOrder
      }
      return a.storeName.localeCompare(b.storeName)
    })
  }, [companyWithStores])

  const filteredStores = useMemo(() => {
    const normalizedStoreName = storeNameQuery.trim().toLowerCase()
    const normalizedCompanyName = companyNameQuery.trim().toLowerCase()
    const selectedCitySet = new Set(selectedCities)
    return availableStores.filter((store) => {
      const matchesCity = !selection.allowCities
        || selectedCities.length === 0
        || selectedCitySet.has(store.city)
      const matchesStoreName = normalizedStoreName.length === 0
        || store.storeName.toLowerCase().includes(normalizedStoreName)
      const matchesCompanyName = normalizedCompanyName.length === 0
        || store.companyName.toLowerCase().includes(normalizedCompanyName)
      return matchesCity && matchesStoreName && matchesCompanyName
    })
  }, [availableStores, storeNameQuery, companyNameQuery, selectedCities, selection.allowCities])

  const filteredStoreIdSet = useMemo(() => {
    return new Set(filteredStores.map((store) => store.storeId))
  }, [filteredStores])

  const eligibleStoreCount = useMemo(() => {
    return countEligibleStores({
      companyWithStores,
      selectedCities,
      selectedStoreIds,
      allowAll: selection.allowAll,
      allowCities: selection.allowCities,
      allowStores: selection.allowStores,
    })
  }, [
    companyWithStores,
    selectedCities,
    selectedStoreIds,
    selection.allowAll,
    selection.allowCities,
    selection.allowStores,
  ])

  const selectedStoreIdSet = useMemo(() => {
    return new Set(selectedStoreIds)
  }, [selectedStoreIds])

  const checkedFilteredStoreCount = useMemo(() => {
    return filteredStores.reduce((count, store) => (
      count + (selectedStoreIdSet.has(store.storeId) ? 1 : 0)
    ), 0)
  }, [filteredStores, selectedStoreIdSet])

  const allFilteredStoresChecked = filteredStores.length > 0
    && checkedFilteredStoreCount === filteredStores.length
  const isFilteredStoresIndeterminate = checkedFilteredStoreCount > 0
    && checkedFilteredStoreCount < filteredStores.length

  const totalStorePages = Math.max(1, Math.ceil(filteredStores.length / storesPerPage))
  const storeStartIndex = storePage * storesPerPage
  const storeEndIndex = storeStartIndex + storesPerPage
  const paginatedStores = filteredStores.slice(storeStartIndex, storeEndIndex)

  useEffect(() => {
    if (isLoading || hasInitialized.current) {
      return
    }

    const selectedCityFilters = filters
      .map((filter) => filter.availableCity)
      .filter((city): city is string => Boolean(city))
    const selectedStoreFilters = filters
      .map((filter) => filter.availableStoreId)
      .filter((storeId): storeId is string => Boolean(storeId))

    const hasCities = selectedCityFilters.length > 0
    const hasStores = selectedStoreFilters.length > 0
    const uniqueStoreIds = Array.from(new Set(selectedStoreFilters))

    setSelection({
      allowAll: !hasCities && !hasStores,
      allowCities: hasCities,
      allowStores: hasStores,
    })
    setSelectedCities(Array.from(new Set(selectedCityFilters)))
    setSelectedStoreIds(uniqueStoreIds)
    hasInitialized.current = true
  }, [filters, isLoading])

  useEffect(() => {
    if (storePage > totalStorePages - 1) {
      setStorePage(Math.max(0, totalStorePages - 1))
    }
  }, [storePage, totalStorePages])

  const handleResetStoreFilters = () => {
    setStoreNameQuery('')
    setCompanyNameQuery('')
  }

  const handleDownloadStoreTemplate = () => {
    const csvContent = buildStoreSelectionTemplateCsv(availableStores)
    downloadCsvFile('fund-source-store-template.csv', csvContent)
  }

  const handleOpenStoreCsvPicker = () => {
    setCsvMessage(null)
    storeCsvInputRef.current?.click()
  }

  const handleLoadStoreCsv: ChangeEventHandler<HTMLInputElement> = async (event) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    try {
      setCsvMessage(null)
      const records = await parseCsvRecords(await file.text())
      if (records.length === 0) {
        setCsvMessage({
          type: 'error',
          text: formatCsvMessage(file.name, 'CSVの内容が空です。'),
        })
        return
      }

      const [headerRow, ...dataRows] = records
      const storeIdColumnIndex = headerRow.indexOf('store_id')
      const storeNameColumnIndex = headerRow.indexOf('store_name')
      const companyNameColumnIndex = headerRow.indexOf('company_name')
      if (storeIdColumnIndex < 0 || storeNameColumnIndex < 0 || companyNameColumnIndex < 0) {
        setCsvMessage({
          type: 'error',
          text: formatCsvMessage(
            file.name,
            'ヘッダーが不正です。store_id,store_name,company_name の3列を含めてください。',
          ),
        })
        return
      }

      const availableStoreIdSet = new Set(availableStores.map((store) => store.storeId))
      const rowErrors: string[] = []
      const parsedStoreIds: string[] = []

      dataRows.forEach((row, rowIndex) => {
        const lineNumber = rowIndex + 2
        const storeId = (row[storeIdColumnIndex] ?? '').trim()
        if (storeId.length === 0) {
          rowErrors.push(`${lineNumber}行目: store_id が空です。`)
          return
        }
        if (!availableStoreIdSet.has(storeId)) {
          rowErrors.push(`${lineNumber}行目: store_id「${storeId}」は存在しません。`)
          return
        }
        parsedStoreIds.push(storeId)
      })

      if (rowErrors.length > 0) {
        setCsvMessage({
          type: 'error',
          text: `${formatCsvMessage(file.name, 'CSVの読み込みに失敗しました。')}\n${rowErrors.join('\n')}`,
        })
        return
      }

      const uniqueStoreIds = Array.from(new Set(parsedStoreIds))
      if (availableStores.length > 0 && uniqueStoreIds.length === availableStores.length) {
        setSelection((current) => updateSelectionState(current, { allowStores: true }))
        setSelectedStoreIds([])
        setCsvMessage({
          type: 'info',
          text: formatCsvMessage(file.name, '全ての店舗が選択されました。店舗利用制限は行いません。'),
        })
        return
      }

      setSelection((current) => updateSelectionState(current, { allowStores: true }))
      setSelectedStoreIds(uniqueStoreIds)
    } catch (error) {
      console.error('Failed to load store CSV:', error)
      setCsvMessage({
        type: 'error',
        text: formatCsvMessage(file.name, 'CSVの読み込みに失敗しました。'),
      })
    } finally {
      event.target.value = ''
    }
  }

  const handleToggleAllFilteredStores = (checked: boolean) => {
    if (checked) {
      setSelectedStoreIds((current) => {
        const next = new Set(current)
        for (const store of filteredStores) {
          next.add(store.storeId)
        }
        return Array.from(next)
      })
      return
    }
    setSelectedStoreIds((current) => current.filter((storeId) => !filteredStoreIdSet.has(storeId)))
  }

  const handleSave = async () => {
    const hasCityFilter = selection.allowCities && selectedCities.length > 0
    const hasStoreFilter = selection.allowStores && selectedStoreIds.length > 0
    const nextCities = selection.allowAll || !hasCityFilter ? [] : selectedCities
    const nextStores = selection.allowAll || !hasStoreFilter ? [] : selectedStoreIds

    try {
      await setFundSourceFilters.mutateAsync({
        fundSourceId,
        availableCities: nextCities,
        availableStoreIds: nextStores,
      })
      navigate({ to: '/admin/fundSources' })
    } catch (error) {
      console.error('Failed to set fund source filters:', error)
    }
  }

  if (isLoading) {
    return (
      <div className={contentWrapper()}>
        <LoadingOverlay />
      </div>
    )
  }

  if (!fundSource) {
    return null
  }

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>
          <Link to="/admin/fundSources">
            <ArrowBack />
          </Link>
          利用先の設定
        </h1>
      </div>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      })}>
        <div className={css(
          sectionWrapper.raw(),
          {
            backgroundColor: 'transparent'
          }
        )}>
          <div className={css({
            display: 'grid',
            gap: '12px',
          })}>
            {[
              { label: 'ポイント名', value: fundSource.name || '-' },
              { label: 'ポイントタイプ', value: getFundSourceTypeLabel(fundSource.type) },
              { label: '譲渡', value: fundSource.forbidTransfer ? '不可' : '可' },
            ].map((row) => (
              <div
                key={row.label}
                className={css({
                  display: 'grid',
                  gridTemplateColumns: '160px 1fr',
                  gap: '8px',
                  fontSize: '14px',
                  color: 'text.primary',
                })}
              >
                <span className={css({ color: 'text.secondary' })}>{row.label}</span>
                <span>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={sectionWrapper()}>
          <div className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          })}>
            <div className={inputLabel()}>
              ポイント利用先制限
              <span className={requiredFieldLabel()}>必須</span>
            </div>
            <div className={description()}>
              <p>ポイントの利用先を制限できます。</p>
              <p>利用可能店舗数: {eligibleStoreCount}店舗</p>
            </div>
          </div>
          <div className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          })}>
            <Checkbox
              label="全店舗利用可能"
              checked={selection.allowAll}
              onChange={(checked) => {
                setSelection((current) => updateSelectionState(current, { allowAll: checked }))
              }}
            />
            <Checkbox
              label="特定地域のみ利用可"
              checked={selection.allowCities}
              onChange={(checked) => {
                setSelection((current) => updateSelectionState(current, { allowCities: checked }))
              }}
            />
            {selection.allowCities && (
              <div
                className={css({
                  ...sectionWrapper.raw(),
                  backgroundColor: 'surface.secondary',
                  gap: '8px',
                })}
              >
                <div
                  className={css({
                    display: 'grid',
                    gap: '8px 16px',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                  })}
                >
                  {availableCities.length === 0 ? (
                    <span className={css({ fontSize: '14px', color: 'text.secondary' })}>
                      選択できる地域がありません。
                    </span>
                  ) : (
                    availableCities.map((city) => (
                      <Checkbox
                        key={city}
                        label={trimPrefecture(city)}
                        checked={selectedCities.includes(city)}
                        onChange={(checked) => {
                          setSelectedCities((current) => {
                            if (checked) {
                              return current.includes(city) ? current : [...current, city]
                            }
                            return current.filter((item) => item !== city)
                          })
                        }}
                      />
                    ))
                  )}
                </div>
              </div>
            )}
            <Checkbox
              label="一部店舗のみ利用可能"
              checked={selection.allowStores}
              onChange={(checked) => {
                setSelection((current) => updateSelectionState(current, { allowStores: checked }))
              }}
            />
            {selection.allowStores && (
              <div
                className={css({
                  ...sectionWrapper.raw(),
                  backgroundColor: 'surface.secondary',
                  gap: '12px',
                })}
              >
                <div className={css({
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '8px',
                  alignItems: 'end',
                  flexWrap: 'wrap',
                })}>
                  <div className={css({
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    width: '240px',
                  })}>
                    <div className={css({ fontSize: '12px' })}>店舗名</div>
                    <input
                      type="text"
                      placeholder="例）○○ストア"
                      className={input()}
                      value={storeNameQuery}
                      onChange={(event) => setStoreNameQuery(event.target.value)}
                    />
                  </div>
                  <div className={css({
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    width: '240px',
                  })}>
                    <div className={css({ fontSize: '12px' })}>事業者名</div>
                    <input
                      type="text"
                      placeholder="例）○○コーポレーション"
                      className={input()}
                      value={companyNameQuery}
                      onChange={(event) => setCompanyNameQuery(event.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className={button()}
                    onClick={handleResetStoreFilters}
                    disabled={storeNameQuery.length === 0 && companyNameQuery.length === 0}
                  >
                    <RestartAlt />
                    リセット
                  </button>
                </div>
                <input
                  ref={storeCsvInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  style={{ display: 'none' }}
                  onChange={handleLoadStoreCsv}
                />
                <div className={css({
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                })}>
                  <div className={css({ fontSize: '12px' })}>CSVから一括選択</div>
                  <div className={css({
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  })}>
                    <button
                      type="button"
                      className={button()}
                      onClick={handleDownloadStoreTemplate}
                    >
                      CSVテンプレートをダウンロード
                    </button>
                    <button
                      type="button"
                      className={button()}
                      onClick={handleOpenStoreCsvPicker}
                    >
                      CSVファイルを読み込み
                    </button>
                  </div>
                </div>
                {csvMessage && (
                  <div className={css({
                    fontSize: '12px',
                    color: csvMessage.type === 'error' ? 'text.accentError' : 'text.secondary',
                    whiteSpace: 'pre-wrap',
                  })}
                  >
                    {csvMessage.text}
                  </div>
                )}
                <div className={tableWrapper()}>
                  <TablePaginationToolbar
                    page={storePage}
                    totalPages={totalStorePages}
                    onPageChange={setStorePage}
                    leading={<div>選択中の店舗: {checkedFilteredStoreCount} / {filteredStores.length}</div>}
                  />
                  <table className={table()}>
                    <thead className={tableHead({ disabled: filteredStores.length === 0 })}>
                      <tr>
                        <th className={css({
                          width: '56px',
                          padding: '0 24px',
                        })}>
                          <div className={css({
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          })}>
                            <Checkbox
                              checked={allFilteredStoresChecked}
                              indeterminate={isFilteredStoresIndeterminate}
                              disabled={filteredStores.length === 0}
                              aria-label="表示中の店舗をすべて選択"
                              onChange={handleToggleAllFilteredStores}
                            />
                          </div>
                        </th>
                        <th className={tableHeadCell()}>店舗名</th>
                        <th className={tableHeadCell()}>事業者名</th>
                        <th className={tableHeadCell()}>市区町村</th>
                        <th className={tableHeadCell()}>登録住所</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedStores.map((store) => (
                        <tr className={tableRow()} key={store.storeId}>
                          <td className={tableCell()}>
                            <Checkbox
                              label=""
                              checked={selectedStoreIds.includes(store.storeId)}
                              aria-label={`${store.storeName}を利用可能にする`}
                              onChange={(checked) => {
                                setSelectedStoreIds((current) => {
                                  if (checked) {
                                    return current.includes(store.storeId) ? current : [...current, store.storeId]
                                  }
                                  return current.filter((item) => item !== store.storeId)
                                })
                              }}
                            />
                          </td>
                          <td className={tableCell()}>{store.storeName || '-'}</td>
                          <td className={tableCell()}>{store.companyName || '-'}</td>
                          <td className={tableCell()}>
                            {store.city ? trimPrefecture(store.city) : '-'}
                          </td>
                          <td className={tableCell()}>{store.address}</td>
                        </tr>
                      ))}
                    </tbody>
                    {filteredStores.length === 0 && (
                      <tbody>
                        <tr className={tableRow()}>
                          <td
                            colSpan={5}
                            className={css({
                              textAlign: 'center',
                              padding: '16px',
                              color: 'text.secondary',
                            })}
                          >
                            条件に一致する店舗がありません。
                          </td>
                        </tr>
                      </tbody>
                    )}
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={css({
          display: 'flex',
        })}>
          <button
            type="button"
            className={button({ color: 'primary' })}
            onClick={handleSave}
            disabled={setFundSourceFilters.isPending}
          >
            設定を保存
            {setFundSourceFilters.isPending && <LoadingOverlay size="sm" />}
          </button>
        </div>
      </div>
    </div>
  )
}
